import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RecordingUploadSession } from '../../shared/core/models/upload-session.model';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { ResourceUploadStatus } from '../../shared/core/state/resource/models';

import * as LiveClassActions from '../../shared/core/state/live-class/live-class.action';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import { LiveClassResponseDTO } from '../../shared/core/state/live-class/models/live-class';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubscriptionStatus } from '../../shared/core/state/subscription/subscription.model';
import { AuthService } from '../../shared/services/auth.service';
import { formatSocialLink } from '../../shared/util/helper.utils';


@Component({
  selector: 'black-live-class-contents',
  standalone: false,
  templateUrl: './live-class-contents.component.html',
  styleUrl: './live-class-contents.component.css',
})
export class LiveClassContentsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private liveClassService = inject(LiveClassService);
  private route = inject(ActivatedRoute);
  private store = inject<Store<State>>(Store);
  private authService = inject(AuthService);
  private destroy$ = new Subject<void>();

  sessionRecordings: RecordingUploadSession[] = [];
  selectedLiveClassId = '';
  selectedLiveClass$: Observable<LiveClassResponseDTO | null> =
    new Observable<LiveClassResponseDTO | null>();
  selectedLiveClass: LiveClassResponseDTO | null = null;
  isAuthorized = false;
  isUserInstructor = false;

  ngOnInit(): void {
    this.selectedLiveClassId = this.route.snapshot.params['id'];
    this.fetchLiveClassDetails();
    this.fetchAllPastRecordings();

    // Subscribe to live class changes and update authorization
    this.selectedLiveClass$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (liveClass) => {
        this.selectedLiveClass = liveClass;
        if (liveClass) {
          await this.updateAuthorizationStatus();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  playVideo(sessionId: string) {
    this.router.navigate(['past-meetings', 'play', sessionId]);
  }

  navigateToHome() {
    this.router.navigate(['my-learnings']);
  }

  fetchLiveClassDetails() {
    if (this.selectedLiveClassId) {
      this.store.dispatch(
        LiveClassActions.selectLiveClass({
          id: this.selectedLiveClassId,
        })
      );
      this.selectedLiveClass$ = this.store.select(
        (state) => state.liveClass.selectedLiveClassItem
      );
    }
  }

  // TODO: move to the relevant component
  fetchAllPastRecordings() {
    if (this.selectedLiveClassId)
      this.liveClassService
        .getAllRecordings(this.selectedLiveClassId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.sessionRecordings = res
            .map((recording) => {
              return {
                sessionId: recording._id,
                sessionDate: recording.sessionDate,
                sessionName: recording.sessionName,
                resource: recording.resource,
                liveClass: recording.liveClass,
                meetingId: recording.meetingId,
              };
            })
            .filter(
              (recording) =>
                recording.resource.uploadStatus ===
                ResourceUploadStatus.COMPLETED
            );
        });
  }

  // Check if user has valid active subscription
  hasValidSubscription(): boolean {
    if (!this.selectedLiveClass) {
      return false;
    }

    const subscription = this.selectedLiveClass.subscriptions;
    return subscription.some((sub) =>
      [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(
        sub.status
      )
    );
  }

  // Check if user is the instructor/owner of the live class
  private async isInstructor(): Promise<boolean> {
    if (!this.selectedLiveClass) {
      return false;
    }

    const currentUser = await this.authService.getDecodedToken();
    const liveClassOwnerId = this.selectedLiveClass.user;

    if (currentUser?.user_id && liveClassOwnerId) {
      // Handle both string and object formats for user field
      const ownerId =
        typeof liveClassOwnerId === 'string'
          ? liveClassOwnerId
          : (liveClassOwnerId as unknown as { _id?: string; id?: string })
              ._id ||
            (liveClassOwnerId as unknown as { _id?: string; id?: string }).id;

      return currentUser.user_id === ownerId;
    }

    return false;
  }

  // Update authorization status based on instructor status and subscription
  private async updateAuthorizationStatus(): Promise<void> {
    const isInstructor = await this.isInstructor();
    this.isUserInstructor = isInstructor;
    const hasSubscription = this.hasValidSubscription();
    this.isAuthorized = isInstructor || hasSubscription;
  }



  get isPendingVerification(): boolean {
    const subs = this.selectedLiveClass?.subscriptions;
    if (!Array.isArray(subs) || subs.length === 0) return false;
    const hasActive = subs.some((s) =>
      [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(s.status)
    );
    return !hasActive && subs.some((s) => s.status === SubscriptionStatus.PENDING);
  }

  get singleEventPass(): any | null {
    if (this.hasFullCourseAccess) return null;
    const subs = this.selectedLiveClass?.subscriptions;
    if (!Array.isArray(subs)) return null;
    return (
      subs.find(
        (s) =>
          s.paymentType === 'SINGLE_EVENT' &&
          [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(s.status)
      ) || null
    );
  }

  get hasFullCourseAccess(): boolean {
    const subs = this.selectedLiveClass?.subscriptions;
    if (!Array.isArray(subs)) return false;
    return subs.some(
      (s) =>
        ['SINGLE', 'ANNUAL', 'ONE_TIME', 'MONTHLY'].includes(s.paymentType) &&
        [SubscriptionStatus.ACTIVE, SubscriptionStatus.COMPLETE].includes(s.status)
    );
  }

  getSocialLink(url: string | undefined, type: 'whatsapp' | 'telegram'): string {
    return formatSocialLink(url, type);
  }
}

